'use client';
import { addEventParam, useQueuedEventStore } from '@ninjacart/nc-events-sdk-v2';
import { fetchTranscriptPlainText, loadScript } from '@ninjacart/nc-nk-core';
import { KissanEventType, useFeedStore } from '@ninjacart/nc-nk-store';
import { useRouter } from '@ninjacart/nc-nk-web-deps';
import { PauseCircle, PlayCircle } from '@tamagui/lucide-icons';
import { forwardRef, memo, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IconChevronLeft } from '../assets/icons';
import IconButton from '../components/iconButton';
import { VideoWebRef } from './videoRef';

let firstClick = true;
let lastSeekEventTime = 0;
const SEEK_THROTTLE_DELAY = 800;
let seekStartTime = 0;
let seekEndTime = 0;

export const Video = memo(
  forwardRef<VideoWebRef, any>((props, ref) => {
    const {
      source,
      playList,
      paused = true,
      muted = true,
      volume,
      rate,
      repeat,
      controls,
      showNotificationControls = false,
      poster,
      fullscreen,
      fullscreenAutorotate,
      fullscreenOrientation,
      onBuffer,
      onLoad,
      onProgress,
      onPlaybackRateChange,
      onError,
      onReadyForDisplay,
      onSeek,
      onVolumeChange,
      onEnd,
      onPause,
      onPlaybackStateChanged,
      adTagUrl,
      type,
      style,
      captions = [],
      screenName,
      transcriptUrl,
      onTranscriptChange,
    } = props;

    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<any>(null);
    const plyrRef = useRef<any>(null);
    const setFeedVideoMuteEnable = useFeedStore((state) => state.setFeedVideoMuteEnable);
    const feedVideoMuteEnable = useFeedStore((state) => state.feedVideoMuteEnable);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isControlsVisible, setIsControlsVisible] = useState(false);
    const { back } = useRouter();
    const videoSource: string = source?.uri ?? source;
    const videoId: string = source?.videoId ?? '-1';
    const processEvents = useRef(useQueuedEventStore.getState().processEvents).current;
    // const pathname = usePathname();
    const watchedTimeRef = useRef(0);
    const lastPlayedTimeRef = useRef<number | null>(null);
    const watchingInterval = useRef<any>(null);
    const seekableTimeRef = useRef<number>(0);
    const currentTimeRef = useRef<number>(0);
    const isScrubbingRef = useRef(false);
    const seekStartTimeRef = useRef<number | null>(null);
    const seekEndTimeRef = useRef<number | null>(null);
    const isSeeking = useRef(false);
    const lastSeekKnownTime = useRef(0);
    const throttleSeekEndEvents = useRef<NodeJS.Timeout>();
    let bufferStartTime: number = NaN;

    const ICON_SIZE = 20;

    const resolvedTranscriptUrl = transcriptUrl || captions?.[0]?.uri || captions?.[0]?.fileUrl;

    useEffect(() => {
      let cancelled = false;
      const url = resolvedTranscriptUrl;

      onTranscriptChange?.('');

      if (!url) {
        return () => {
          cancelled = true;
        };
      }

      (async () => {
        try {
          const text = await fetchTranscriptPlainText(url);

          if (!cancelled && text) {
            onTranscriptChange?.(text);
          }
        } catch (error) {
          if (!cancelled) {
            console.log('error loading transcript', error);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [resolvedTranscriptUrl, onTranscriptChange]);

    useImperativeHandle(ref, () => ({
      seek: (time: number) => {
        const video = videoRef.current;
        if (video) video.currentTime = time;
      },
      resume: () => plyrRef.current?.play(),
      pause: () => plyrRef.current?.pause(),
      presentFullscreenPlayer: () => plyrRef.current?.fullscreen?.enter(),
      dismissFullscreenPlayer: () => plyrRef.current?.fullscreen?.exit(),
      restoreUserInterfaceForPictureInPictureStopCompleted: () => {},
      setVolume: (volume: number) => {
        if (plyrRef.current) {
          plyrRef.current.volume = volume;
          plyrRef.current.muted = volume === 0;
          setFeedVideoMuteEnable(volume === 0);
        }
      },
      setFullScreen: (fullScreen: boolean) => {
        if (fullScreen) plyrRef.current?.fullscreen?.enter();
        else plyrRef.current?.fullscreen?.exit();
      },
      save: (options: object) => Promise.resolve({ uri: '' }),
      getCurrentPosition: () => plyrRef.current?.currentTime ?? 0,
      setSource: (source?: any) => {},
      enterPictureInPicture: () => {},
      exitPictureInPicture: () => {},
      isMuted: () => plyrRef.current?.muted ?? false,
      seeking: () => isScrubbingRef.current ?? false,
      toggleMute: () => {
        if (plyrRef.current) {
          const newMutedState = !plyrRef.current.muted;
          plyrRef.current.muted = newMutedState;
          setFeedVideoMuteEnable(newMutedState);
          return newMutedState;
        }
        return false;
      },
    }));

    // const onSeeking = () => {
    //   const now = Date.now();
    //   if (plyrRef.current?.seeking && now - lastSeekEventTime > SEEK_THROTTLE_DELAY) {
    //     seekStartTime = currentTimeRef.current ?? -1;
    //     lastSeekEventTime = now;

    //     processEvents([
    //       {
    //         name: KissanEventType.AUTOPLAY_SEEK_STARTED,
    //         screenDetails: { screen: 'video player' },
    //         eventParams: [addEventParam('url', videoSource), addEventParam('videoId', videoId)],
    //       },
    //     ]);
    //   }
    // };

    const initializePlyr = async () => {
      if (!(window as any).Plyr) {
        await loadScript('https://cdnjs.cloudflare.com/ajax/libs/plyr/3.7.8/plyr.min.js');
      }

      const Plyr = (window as any).Plyr;

      const plyrControls = controls
        ? [
            'play',
            'progress',
            'current-time',
            'mute',
            'volume',
            'settings',
            'fullscreen',
            'captions',
          ]
        : ['progress', 'current-time'];

      plyrRef.current = new Plyr(videoRef.current, {
        controls: plyrControls,
        settings: ['captions', 'quality', 'speed', 'loop'],
        autoplay: true,
        muted: feedVideoMuteEnable,
        volume: 0.5,
        clickToPlay: false,
        hideControls: plyrControls.length !== 2,
        captions: {
          active: true,
          update: true,
          language: 'en', // Default language
        },
        tracks: captions?.map((caption) => ({
          kind: 'subtitles',
          src: caption.fileUrl,
          srclang: caption.languageCode,
          label: caption.languageLabel,
          default: caption.languageCode === 'en', // Set default language if needed
        })),
      });

      // ✅ Volume change
      plyrRef.current.on('volumechange', () => {
        setFeedVideoMuteEnable(plyrRef.current.muted);
      });

      plyrRef.current.on('seeking', () => {
        isSeeking.current = true;
        if (!seekStartTimeRef.current) {
          seekStartTimeRef.current = plyrRef.current?.currentTime;
        }
      });

      // ✅ Seek start
      plyrRef.current.on('seeked', async () => {
        // if (!isSeeking.current) {
        //   await processEvents([
        //     {
        //       name: KissanEventType.AUTOPLAY_SEEK_STARTED,
        //       screenDetails: { screen: 'video player' },
        //       eventParams: [addEventParam('url', videoSource), addEventParam('videoId', videoId)],
        //     },
        //   ]);
        // }

        if (isSeeking.current && !videoRef.current?.seeking) {
          clearTimeout(throttleSeekEndEvents.current);
          throttleSeekEndEvents.current = setTimeout(async () => {
            await processEvents([
              {
                name: KissanEventType.AUTOPLAY_SEEK_COMPLETED,
                screenDetails: {
                  screen: (screenName ?? '').toLowerCase().includes('discovery')
                    ? 'discovery page'
                    : 'feed video',
                },
                eventParams: [
                  addEventParam('url', videoSource),
                  addEventParam('videoId', videoId),
                  addEventParam('referenceKey1', 'seekStartTime'),
                  addEventParam('referenceValue1', seekStartTimeRef.current?.toFixed(3)),
                  addEventParam('referenceKey2', 'seekEndTime'),
                  addEventParam('referenceValue2', plyrRef.current?.currentTime.toFixed(3)),
                ],
              },
            ]);

            isScrubbingRef.current = false;
          }, SEEK_THROTTLE_DELAY);

          isSeeking.current = false;
        }
        isScrubbingRef.current = true;
        // stopWatching();
      });

      // ✅ Playback state
      plyrRef.current.on('playing', async () => {
        // setIsPlaying(true);
        // onPlaybackStateChanged?.({ isPlaying: true });
      });

      plyrRef.current.on('pause', () => {
        if (plyrRef.current.seeking) {
          isSeeking.current = plyrRef.current.seeking;
          // seekStartTimeRef.current = lastSeekKnownTime.current;
        }
        setIsPlaying(false);
        // stopWatching();
        // onPause?.();
      });
    };

    const handleAutoPlayEvents = (eventName: string, reason: any) => {
      processEvents([
        {
          name: eventName,
          screenDetails: { screen: 'video player' },
          eventParams: [addEventParam('url', videoSource), addEventParam('errorMessage', reason)],
        },
      ]);
    };

    const handleBufferEnd = async () => {
      if (!isNaN(bufferStartTime)) {
        const bufferEndTime = performance.now() - bufferStartTime;
        onPlaybackStateChanged?.({ isPlaying: true, buffered: (bufferEndTime / 1000).toFixed(3) });
        bufferStartTime = NaN;
        // console.log('With buffered time => ', bufferEndTime);
        return;
      }
      onBuffer?.(false);
      onPlaybackStateChanged?.({ isPlaying: true });
      // await initializePlyr();
    };

    const handleWaiting = () => {
      bufferStartTime = performance.now();
    };

    const startWatching = () => {
      if (watchingInterval.current) return;
      watchingInterval.current = setInterval(() => {
        const current = Math.floor(currentTimeRef.current);
        if (!isScrubbingRef.current && typeof current === 'number') {
          if (lastPlayedTimeRef.current === null) {
            lastPlayedTimeRef.current = current;
            return;
          }
          if (current - lastPlayedTimeRef.current === 1) {
            watchedTimeRef.current += 1;
          }
          lastPlayedTimeRef.current = current;
        }
      }, 500);
    };

    const stopWatching = () => {
      if (watchingInterval.current) {
        clearInterval(watchingInterval.current);
        watchingInterval.current = null;
      }
    };

    const initializePlayer = async () => {
      // console.log('Starting to initialize!!!');
      const video = videoRef.current;
      if (!video || !videoSource) {
        // console.log('Return no initialize!!!');
        return;
      }

      if (hlsRef.current || plyrRef.current) {
        // console.log('Return no initialize 2!!!');
        return;
      }

      await loadScript('https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.18/hls.light.min.js');
      const Hls = (window as any).Hls;

      if (Hls && Hls.isSupported()) {
        // console.log('HLS available and initializing!!!');
        const hls = new Hls({
          capLevelToPlayerSize: true,
          maxBufferLength: 30, // Max buffer length in seconds (default: 300)
          maxMaxBufferLength: 60, // Hard cap on max buffer length (default: 600)
          maxBufferSize: 2 * 1000 * 1000, // Max buffer size in bytes (30MB)
          maxBufferHole: 0.5, // How much gap is allowed before buffering resumes
          lowBufferWatchdogPeriod: 0.5, // Watchdog check period for low buffer
          highBufferWatchdogPeriod: 1, // Check period for high buffer
          backBufferLength: 30, // Keep only 30s behind current playhead
          backBufferRetentionTime: 30,
        });
        hls.loadSource(videoSource);
        hls.attachMedia(video);
        hlsRef.current = hls;

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          // console.log('Manifest parsed and ready !!!');
          video.muted = feedVideoMuteEnable;
          video.play().catch((err) => {
            console.log('Error in playback => ', err);
            handleAutoPlayEvents(KissanEventType.AUTOPLAY_VIDEO_FAIL_BEFORE_INTERACTION, err);
          });
        });
      } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
        video.src = videoSource;
      }

      // console.log('Attahcing video event handlers!!!');

      video.addEventListener('waiting', handleWaiting);
      video.addEventListener('playing', handleBufferEnd);
      video.addEventListener('timeupdate', handleTimeUpdate);
      video.addEventListener('play', handlePlay);
      video.addEventListener('pause', handlePause);
      video.addEventListener('loadstart', handleLoadStart);
      video.addEventListener('ended', handleEnd);
      video.addEventListener('error', handleError);
      // video.addEventListener('seeking', handleSeeking);
      // video.addEventListener('seeked', handleSeeked);
      video.addEventListener('loadedmetadata', handleLoadedMetadata);
    };

    useEffect(() => {
      const handleVisibilityChange = () => {
        if (document.hidden && plyrRef.current) {
          plyrRef.current.pause();
          return;
        }

        if (!document.hidden && plyrRef.current) {
          const currentTime = plyrRef.current.currentTime;
          plyrRef.current.play();
        }
      };

      const video = videoRef.current;

      if (video && !hlsRef.current && !plyrRef.current) {
        // console.log('Initializing player instaces!!!');
        // localStorage.removeItem('plyr');
        initializePlayer();
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }

      return () => {
        // console.log('Video instance cleaning up!!!');
        const video = videoRef.current;
        if (video) {
          video.removeEventListener('waiting', handleWaiting);
          video.removeEventListener('playing', handleBufferEnd);
          video.removeEventListener('timeupdate', handleTimeUpdate);
          video.removeEventListener('play', handlePlay);
          video.removeEventListener('pause', handlePause);
          video.removeEventListener('loadstart', handleLoadStart);
          video.removeEventListener('ended', handleEnd);
          video.removeEventListener('error', handleError);
          // video.removeEventListener('seeking', handleSeeking);
          // video.removeEventListener('seeked', handleSeeked);
          video.removeEventListener('loadedmetadata', handleLoadedMetadata);
        }

        document.removeEventListener('visibilitychange', handleVisibilityChange);

        hlsRef.current?.destroy();
        hlsRef.current = null;
        plyrRef.current?.destroy();
        plyrRef.current = null;
        stopWatching();
      };
    }, [videoSource]);

    const handleTimeUpdate = () => {
      const video = videoRef.current;

      if (video) {
        currentTimeRef.current = video.currentTime;
        const jump = Math.abs(video.currentTime - lastSeekKnownTime.current);
        // console.log('Is seeking => ', plyrRef.current?.seeking);

        if (jump > 3 && plyrRef.current?.seeking) {
          seekStartTimeRef.current = lastSeekKnownTime.current;
          // console.log(
          //   '🚀 ~ handleTimeUpdate ~ seekStartTimeRef.current:',
          //   seekStartTimeRef.current
          // );
        } else {
          lastSeekKnownTime.current = video.currentTime;
        }

        const playableTime =
          video.buffered.length > 0 ? video.buffered.end(video.buffered.length - 1) : 0;
        seekableTimeRef.current =
          video.seekable.length > 0 ? video.seekable.end(video.seekable.length - 1) : 0;

        if (onProgress && !isScrubbingRef.current) {
          startWatching();
          onProgress({
            currentTime: watchedTimeRef.current,
            playableTime,
            seekableTime: seekableTimeRef.current,
          });
        }
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      // onPlaybackStateChanged?.({ isPlaying: true });
    };

    const handlePause = () => {
      stopWatching();
      onPause?.();
    };

    // const handleSeeking = () => {
    //   const video = videoRef.current;
    //   console.log('Seeking active => ', video?.currentTime);
    //   if (video && seekStartTimeRef.current === null) {
    //     seekStartTimeRef.current = video.currentTime;
    //     console.log('Seek started at:', seekStartTimeRef.current);
    //   }
    //   isScrubbingRef.current = true;
    //   stopWatching();
    // };

    // const handleSeeked = () => {
    //   const video = videoRef.current;
    //   if (video && seekStartTimeRef.current !== null && !plyrRef.current?.seeking) {
    //     seekEndTimeRef.current = video.currentTime;
    //     console.log('Seek ended at:', seekEndTimeRef.current);

    //     processEvents([
    //       {
    //         name: KissanEventType.AUTOPLAY_SEEK_COMPLETED,
    //         screenDetails: { screen: 'video player' },
    //         eventParams: [
    //           addEventParam('url', videoSource),
    //           addEventParam('videoId', videoId),
    //           addEventParam('referenceKey1', 'seekStartTime'),
    //           addEventParam('referenceValue1', seekStartTimeRef.current.toFixed(3)),
    //           addEventParam('referenceKey2', 'seekEndTime'),
    //           addEventParam('referenceValue2', seekEndTimeRef.current.toFixed(3)),
    //         ],
    //       },
    //     ]);

    //     // Reset for next seek operation
    //     seekStartTimeRef.current = null;
    //     seekEndTimeRef.current = null;
    //   }

    //   isScrubbingRef.current = false;
    //   lastPlayedTimeRef.current = Math.floor(currentTimeRef.current);
    //   startWatching();
    // };

    const handleLoadedMetadata = async () => {
      // console.log('Meta data load is also complete!!!');
      if (!plyrRef.current) {
        await initializePlyr();
      }
    };

    const handleLoadStart = (data: any) => onLoad?.(data);
    const handleEnd = () => {
      stopWatching();
      onEnd?.();
    };
    const handleError = (error: any) => onError?.(error);

    const handleBackButtonClick = () => back();

    const handlePlayButtonClick = () => {
      const video = videoRef.current;
      if (video) {
        if (isPlaying) {
          setIsControlsVisible(true);
          setIsPlaying(false);
          video.pause();
        } else {
          setIsControlsVisible(false);
          video.play();
        }
      }
    };

    const onVideoClick = (event: any): void => {
      if (plyrRef.current) {
        const plyr = plyrRef.current;
        if (plyr.muted) {
          plyr.muted = false;
          event.stopPropagation();
          return;
        }

        if (plyr.seeking) {
          console.log('Plyr is seeking all touch events are not propagated!!!');
          event.stopPropagation();
          return;
        }
      }
    };

    return (
      <div>
        <link
          rel="stylesheet"
          href="https://cdnjs.cloudflare.com/ajax/libs/plyr/3.7.8/plyr.min.css"
        />
        <div
          style={{
            position: 'relative',
            width: '100%',
            height: style?.['height'] || '100%',
            maxWidth: '800px',
            margin: '0 auto',
            backgroundColor: 'black',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              height: style?.['height'] || '100%',
            }}
            // onClick={onVideoClick}
          >
            <video
              ref={videoRef}
              style={{ width: '100%', height: style?.['height'] || '100%' }}
              controls={controls && isControlsVisible}
              playsInline
              poster={poster}
              muted={feedVideoMuteEnable}
              autoPlay
              crossOrigin="anonymous"
              aria-describedby="video-transcript"
            >
              {captions?.length > 0 && (
                <track
                  kind="subtitles"
                  srcLang={captions[0].language || 'en'}
                  label={captions[0].title || 'English'}
                  src={captions[0].uri}
                  default
                />
              )}
            </video>
            {(isControlsVisible || !isPlaying) && controls && (
              <>
                <button
                  onClick={handlePlayButtonClick}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    border: 'none',
                    borderRadius: '50%',
                    padding: '20px',
                    cursor: 'pointer',
                    zIndex: 10,
                    background: 'transparent',
                  }}
                >
                  {isPlaying ? (
                    <PauseCircle size={50} color={'#FFFFFF'} />
                  ) : (
                    <PlayCircle size={40} color={'#FFFFFF'} />
                  )}
                </button>
                <div
                  style={{
                    position: 'absolute',
                    top: '0px',
                    left: '0px',
                    right: '0px',
                    padding: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    zIndex: 10,
                    color: '#FFFFFF',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  }}
                >
                  <IconButton onPress={handleBackButtonClick}>
                    <IconChevronLeft size={ICON_SIZE} color={'white'} />
                  </IconButton>
                </div>
              </>
            )}
          </div>
          <div id="autoplayinteract"></div>
        </div>
      </div>
    );
  })
);

// export default memo(Video);
